// Keyboard-focus bookkeeping for the bookmark list.
//
// The list has no React `focusedIndex` state: the "cursor" is a module-level id
// plus a CSS class on the row, moved imperatively. Two callers share it —
// useBookmarkListKeyboardNav (arrow keys move the cursor) and
// useBookmarkRowShortcuts (row actions read it, and advance it when the action
// removes the focused row from the list). A module store (like
// readerFocusIntent) keeps them in sync without threading state through the
// list -> row tree.

// Dedicated CSS class for keyboard focus so we don't interfere with React's own
// "active" class used for the currently-reading bookmark.
const FOCUSED_CLASS = 'keyboard-focused';

let focusedBookmarkId: string | null = null;

/** The row the 2col keyboard cursor is on, or null when there is no cursor. */
function getFocusedBookmarkId(): string | null {
  return focusedBookmarkId;
}

function setFocusedBookmarkId(id: string | null): void {
  focusedBookmarkId = id == null ? null : String(id);
}

/** Remove the keyboard-focus highlight from whichever row currently carries it. */
function clearFocusedRow(): void {
  document
    .querySelector(`.bookmark-item.${FOCUSED_CLASS}`)
    ?.classList.remove(FOCUSED_CLASS);
}

/** Drop the cursor entirely: forget the id and remove the highlight. */
function resetBookmarkListFocus(): void {
  setFocusedBookmarkId(null);
  clearFocusedRow();
}

/**
 * Focus the bookmark row for `id` and scroll it into view.
 *
 * - `markHighlight`: also apply the 2col `.keyboard-focused` class so the row
 *   gets the keyboard-nav visual treatment.
 * - `defer`: schedule the focus/scroll in a rAF so layout has settled (used
 *   from handlers that also mutate selection state, which may re-render the
 *   list before we try to focus the target row).
 */
function focusBookmarkRow(
  id: string,
  options: { markHighlight?: boolean; defer?: boolean } = {},
): void {
  const run = () => {
    if (options.markHighlight) clearFocusedRow();
    const wrapper = document.querySelector<HTMLElement>(`[data-bookmark-id="${id}"]`);
    const item = wrapper?.querySelector<HTMLElement>('.bookmark-item');
    if (options.markHighlight) item?.classList.add(FOCUSED_CLASS);
    item?.focus({ preventScroll: true });
    wrapper?.scrollIntoView({ block: 'nearest' });
  };
  if (options.defer) requestAnimationFrame(run);
  else run();
}

export {
  FOCUSED_CLASS,
  clearFocusedRow,
  focusBookmarkRow,
  getFocusedBookmarkId,
  resetBookmarkListFocus,
  setFocusedBookmarkId,
};
